import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { isSupportedLanguage } from '../shared/supported-languages';
import CONFIG from '../config/config';

export const useLanguageDetection = (storeLanguage?: string) => {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const hasInitialized = useRef(false);
  const lastStoreLanguage = useRef<string | undefined>(undefined);

  useEffect(() => {
    const determineLanguage = () => {
      // 1. Highest priority: URL parameter
      const langParam = searchParams.get('lang');
      if (langParam && isSupportedLanguage(langParam)) {
        i18n.changeLanguage(langParam);
        return;
      }

      // 2. Second priority: Store's language from API response
      if (storeLanguage && isSupportedLanguage(storeLanguage)) {
        i18n.changeLanguage(storeLanguage);
        return;
      }

      // 3. Third priority: Browser language detection
      const browserLanguage = navigator.language.split('-')[0];
      if (isSupportedLanguage(browserLanguage)) {
        i18n.changeLanguage(browserLanguage);
        return;
      }

      // 4. Fallback: Default language
      const defaultLanguage = CONFIG.DEFAULT_LANGUAGE.code;
      i18n.changeLanguage(defaultLanguage);
    };

    const shouldRun = !hasInitialized.current || 
                     (storeLanguage && storeLanguage !== lastStoreLanguage.current);

    if (shouldRun) {
      determineLanguage();
      hasInitialized.current = true;
      lastStoreLanguage.current = storeLanguage;
    }
  }, [searchParams, storeLanguage, i18n]);

  const changeLanguage = (languageCode: string) => {
    if (isSupportedLanguage(languageCode)) {
      i18n.changeLanguage(languageCode);
    }
  };

  return {
    currentLanguage: i18n.language,
    changeLanguage,
  };
}; 